import { Grid, useTheme } from "@mui/material";
import { observer } from "mobx-react";
import React, { FunctionComponent, useEffect, useMemo } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { Responsive as ResponsiveGridLayout } from "react-grid-layout/legacy";
import WidgetCard from "../../components/Widgets/Views/WidgetCard";
import { DashboardStore } from "../../stores/Layout/DashboardStore";
import hexToRGB from "../../utils/hexToRGB";
import DashboardControls from "./DashboardControls";
import DashboardEditor from "./DashboardEditor";
import EmptyDashboard from "./EmptyDashboard";
import { useStore } from "utils/useStore";
import { PublishedWidgetStore } from "stores/PublishedWidgetStore";
import AcxEmptyState from "components/UI/AcxEmptyStateIndicator/AcxEmptyState";
import { sxClsx } from "utils/styleClsx";
import ReportsStore from "components/Signals/Reports/Stores/ReportsStore";
import SignalsPageStore from "components/Signals/Store/SignalsPageStore";

interface OwnProps {
    dashboardStore: DashboardStore;
    width: number;
    height: number;

    containerPadding?: [number, number] | { [P: string]: [number, number] };

    customControls?: () => React.ReactNode;
    isSignals?: boolean;
}

type Props = OwnProps;

const columnsPerBreakpoint = { xl: 4, lg: 4, md: 4, sm: 4, xs: 4 };
const defaultContainerPadding = [32, 16] as [number, number];
const dashboardContainerStyle = { height: "calc(100% - 20px)", width: "100%" };

const BaseDashboard: FunctionComponent<Props> = observer((props) => {
    const theme = useTheme();
    useEffect(() => {
        return () => {
            props.dashboardStore.endEditing();
        };
    }, [props.dashboardStore]);

    const breakpoints = useMemo(
        () => ({ ...theme.breakpoints.values }),
        [theme.breakpoints],
    );

    const dashboardItems =
        props.dashboardStore?.dashboardItems?.filter(
            (i) => i.key !== "eval-coaching-table", // this widget is now handled outside the dashboardStore widgets, dont show it to users anymore
            // may want to remove it from db as well?
        ) ?? [];

    const widgetStore = useStore(PublishedWidgetStore);
    const reportStore = useStore(ReportsStore);
    const signalsPageStore = useStore(SignalsPageStore);

    const loadingEvalReportingDash =
        widgetStore.getTaskLoadingV2("Loading Published Widgets") ||
        reportStore.anyTaskLoading ||
        props.dashboardStore.anyTaskLoading ||
        signalsPageStore.getTaskLoading("LoadDashboards"); // need to check for standard dashboard loading when eval reporting is the home page

    return (
        <>
            <DashboardEditor
                dashboardStore={props.dashboardStore}
                showRefresh={
                    Boolean(props.dashboardStore?.layouts) &&
                    Boolean(dashboardItems)
                }
            />
            <DashboardControls
                dashboardStore={props.dashboardStore}
                customControls={props.customControls}
            />
            {props.height &&
            props.dashboardStore.layouts &&
            dashboardItems.length ? (
                <Grid
                    sx={sxClsx(
                        !props.isSignals && {
                            height: "calc(100% - 128px)",
                            width: "100%",
                            position: "relative",
                        },
                        props.isSignals && {
                            height: "calc(100% - 79px)",
                        },
                    )}
                >
                    <Grid
                        sx={sxClsx(
                            dashboardContainerStyle,
                            props.dashboardStore.editMode && {
                                borderRight: `2.7px solid ${hexToRGB(
                                    theme.palette.primary.main,
                                    0.75,
                                )}`,
                                borderLeft: `2.7px solid ${hexToRGB(
                                    theme.palette.primary.main,
                                    0.75,
                                )}`,
                                // borderBottom: `2.7px solid ${hexToRGB(theme.palette.primary.main, .75)}`,
                                borderTop: `2.2px solid ${hexToRGB(
                                    theme.palette.primary.main,
                                    0.75,
                                )}`,
                            },
                            { height: props.height },
                        )}
                    >
                        <Scrollbars autoHide>
                            <ResponsiveGridLayout
                                className="layout"
                                layouts={props.dashboardStore.layouts}
                                width={props.width}
                                rowHeight={50}
                                onBreakpointChange={
                                    props.dashboardStore.onBreakpointChange
                                }
                                onLayoutChange={
                                    props.dashboardStore.onLayoutChange
                                }
                                onDragStart={props.dashboardStore.onDragStart}
                                onDragStop={props.dashboardStore.onDragEnd}
                                breakpoints={breakpoints}
                                autoSize={true}
                                containerPadding={
                                    props.containerPadding ??
                                    defaultContainerPadding
                                }
                                useCSSTransforms={true}
                                preventCollision={false}
                                verticalCompact
                                compactType={"vertical"}
                                cols={columnsPerBreakpoint}
                            >
                                {dashboardItems?.map((value) => {
                                    return (
                                        <Grid
                                            key={value.key}
                                            sx={sxClsx(
                                                props.dashboardStore
                                                    ?.editMode && {
                                                    border: `1px solid ${theme.palette.purple.main}`,
                                                },
                                            )}
                                        >
                                            <WidgetCard
                                                content={
                                                    value.widgetDefinition
                                                        .render
                                                }
                                                styles={
                                                    value.widgetDefinition
                                                        .styles
                                                }
                                                dashboardStore={
                                                    props.dashboardStore
                                                }
                                                disableActions={
                                                    props.dashboardStore
                                                        ?.anyWidgetsLoading
                                                }
                                                widgetDefinition={
                                                    value.widgetDefinition
                                                }
                                            />
                                        </Grid>
                                    );
                                })}
                            </ResponsiveGridLayout>
                        </Scrollbars>
                    </Grid>
                </Grid>
            ) : loadingEvalReportingDash ? (
                <Grid container height="100%">
                    <AcxEmptyState size="large" variant="loading" />
                </Grid>
            ) : (
                <EmptyDashboard
                    parentHeightPx={props.height - 30}
                    dashboardStore={props.dashboardStore}
                />
            )}
        </>
    );
});

export default BaseDashboard;
